"use client";

import { useLocale } from "@/lib/compat/i18n";

/**
 * Locale-aware monetary + date formatters. Currency stays ISO-coded
 * (USD/AED/BRL) so the audience reads it uniformly across locales — no
 * need to localise the currency symbol.
 */

export function useMoneyFormatter() {
  const locale = useLocale();
  return (n: number, currency = "USD"): string => {
    if (n >= 1_000_000) {
      const v = (n / 1_000_000).toFixed(1).replace(/\.0$/, "");
      return `${currency} ${new Intl.NumberFormat(locale).format(Number(v))}M`;
    }
    if (n >= 1_000) {
      const v = (n / 1_000).toFixed(0);
      return `${currency} ${new Intl.NumberFormat(locale).format(Number(v))}K`;
    }
    return `${currency} ${new Intl.NumberFormat(locale).format(n)}`;
  };
}

export function useNumberFormatter() {
  const locale = useLocale();
  return (n: number): string => new Intl.NumberFormat(locale).format(n);
}

export function useDateFormatter(opts?: Intl.DateTimeFormatOptions) {
  const locale = useLocale();
  return (iso: string | Date): string => {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(
      locale,
      opts ?? { year: "numeric", month: "short", day: "numeric" },
    ).format(d);
  };
}
